import { css } from "@compiled/react";
import {
    UniformScrollableCardItem,
    flattenScrollableCardProps
} from "src/uniform/contenttypes/scrollableCardItem";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import {
    Lozenge,
    type LozengeAppearance
} from "@/libraries/design/components/lozenge/src/lozenge";
import { wacToken } from "@/libraries/design/foundations";

/** Maps Uniform scrollable lozenge color (incl. saffron) to design Lozenge appearance. */
const scrollableLozengeColorToAppearance = (
    color: UniformScrollableCardItem["scrollableCardLozengeColor"] | undefined
): LozengeAppearance => {
    switch (color) {
        case "blue":
        case "neutral":
        case "saffron":
        case "lime":
        case "purple":
            return color;
        default:
            return "neutral";
    }
};

export type ScrollableCardsBackgroundColor =
    | "blue100"
    | "primaryBlack"
    | "light100";

export interface UniformScrollableHeroCardsProps {
    scrollableCard: UniformScrollableCardItem[];
    flattenData?: boolean;
    backgroundColor?: ScrollableCardsBackgroundColor;
}

const backgroundColorMap: Record<ScrollableCardsBackgroundColor, string> = {
    blue100: wacToken("color.extended.blue100"),
    primaryBlack: wacToken("color.primary.black"),
    light100: wacToken("color.neutral.light100")
};

const UniformScrollableHeroCards = (props: UniformScrollableHeroCardsProps) => {
    const { flattenData = true, backgroundColor = "blue100" } = props;
    let { scrollableCard } = props;

    if (flattenData) {
        const flattened = flattenScrollableCardProps(props);
        scrollableCard = flattened.scrollableCard;
    }

    const containerBackgroundColor = backgroundColorMap[backgroundColor];

    return (
        <div
            css={containerStyles}
            style={{ background: containerBackgroundColor }}
        >
            <div css={scrollableContainer}>
                {scrollableCard?.map(
                    (card: UniformScrollableCardItem, index: number) => {
                        const cardContent = (
                            <>
                                <Lozenge
                                    appearance={scrollableLozengeColorToAppearance(
                                        card.scrollableCardLozengeColor
                                    )}
                                    text={card.scrollableCardLozengeText}
                                />
                                <h2
                                    className="scrollable-card-title"
                                    css={titleStyles}
                                >
                                    {card.scrollableCardTitle}
                                </h2>
                                <Image
                                    src={card.scrollableCardImage}
                                    alt={card.scrollableCardTitle}
                                />
                            </>
                        );

                        return card.scrollableCardUrl?.path ? (
                            <a
                                key={index}
                                href={card.scrollableCardUrl.path}
                                css={[cardWrapper, clickableCardStyles]}
                                aria-label={
                                    card.scrollableCardUrl.alt ||
                                    card.scrollableCardTitle
                                }
                            >
                                {cardContent}
                            </a>
                        ) : (
                            <div key={index} css={cardWrapper}>
                                {cardContent}
                            </div>
                        );
                    }
                )}
            </div>
        </div>
    );
};

const containerStyles = css({
    paddingBlock: token("space.200"),
    paddingInline: token("space.500"),
    [media.below.md]: {
        paddingInline: token("space.300")
    }
});

const scrollableContainer = css({
    width: "1300px",
    maxWidth: "100%",
    margin: "auto",
    display: "flex",
    scrollPaddingLeft: token("space.100"),
    padding: token("space.100"),
    gap: token("space.300"),
    paddingBlockEnd: token("space.300"),
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    scrollbarWidth: "thin",
    scrollbarColor: `${wacToken("color.extended.blue500")} ${wacToken(
        "color.neutral.light100"
    )}`,
    [`&::-webkit-scrollbar`]: {
        height: token("space.100")
    },
    [`&::-webkit-scrollbar-track`]: {
        background: wacToken("color.neutral.light100"),
        borderRadius: token("space.100")
    },
    [`&::-webkit-scrollbar-thumb`]: {
        background: "hotpink",
        borderRadius: token("space.100")
    },
    [media.below.lg]: {
        overflowX: "scroll"
    },
    [media.below.md]: {
        gap: token("space.200")
    }
});

const titleStyles = css({
    margin: `0 ${token("space.300")} ${token("space.300")} 0`,
    padding: `0 ${token("space.600")} 0 0`,
    [`&.scrollable-card-title`]: {
        fontSize: "1.375rem",
        lineHeight: 1.25
    }
});

const cardWrapper = css({
    scrollSnapAlign: "start",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "302px",
    minWidth: "302px",
    gap: token("space.050"),
    background: wacToken("color.neutral.light0"),
    borderRadius: wacToken("corner.radius.card"),
    boxShadow: token("elevation.shadow.raised"),
    padding: `${token("space.400")} 0 0 ${token("space.400")}`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "100%",
        height: "328px",
        objectFit: "cover",
        objectPosition: "0 0",
        margin: "auto 0 0"
    }
});

const clickableCardStyles = css({
    "textDecoration": "none",
    "color": "unset",
    "cursor": "pointer",
    "transition": "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
        transform: "translateY(-2px) translateX(1px)",
        boxShadow: token("elevation.shadow.overlay")
    },
    "&:focus": {
        outline: `2px solid ${wacToken("color.extended.blue500")}`
    }
});

export default UniformScrollableHeroCards;
